/**
 * Componente WorkloadChart
 * 
 * Gráfico de carga de trabalho por profissional.
 * Wrapper específico para o gráfico de barras de carga de trabalho.
 * 
 * @module components/Dashboard/ChartsSection/WorkloadChart
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaChartBar } from 'react-icons/fa';
import GraficoBarras from '../../Charts/GraficoBarras';
import ChartWrapper from './ChartWrapper';
import chartConfig from '../../../utils/dashboard/chartConfig';

/**
 * Componente de gráfico de carga de trabalho
 * 
 * Renderiza um gráfico de barras mostrando a carga de trabalho por profissional.
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array} props.data - Dados da carga de trabalho
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {Error|string|null} props.error - Erro ocorrido
 * @param {string} [props.periodo] - Período dos dados (ex: "Próximos 7 dias")
 * @param {string} [props.className] - Classes CSS adicionais
 * @param {string} [props.testId] - ID para testes
 * 
 * @returns {JSX.Element} Componente de gráfico
 * 
 * @example
 * ```jsx
 * <WorkloadChart
 *     data={workloadData}
 *     isLoading={isLoading}
 *     error={error}
 *     periodo="Próximos 7 dias"
 * />
 * ```
 */
const WorkloadChart = ({
    data,
    isLoading,
    error,
    periodo = 'Próximos 7 dias',
    className = '',
    testId = null,
}) => {
    const title = (
        <>
            <FaChartBar className="mr-2 text-blue-500 inline" />
            Carga de Trabalho Futura ({periodo})
        </>
    );

    const subtitle = 'Total de tarefas Pendentes/Atrasadas por profissional.';

    return (
        <ChartWrapper
            isLoading={isLoading}
            error={error}
            data={data}
            loadingMessage="Carregando carga de trabalho..."
            errorMessage="Erro ao carregar carga de trabalho."
            emptyMessage="Não há dados de carga de trabalho para exibir."
            className={className}
            testId={testId || 'workload-chart'}
        >
            <GraficoBarras
                data={data}
                dataKey={chartConfig.workloadConfig?.dataKey || 'total_tarefas'}
                nameKey={chartConfig.workloadConfig?.nameKey || 'profissional_nome'}
                title={title}
                subtitle={subtitle}
            />
        </ChartWrapper>
    );
};

WorkloadChart.propTypes = {
    /** Dados da carga de trabalho */
    data: PropTypes.array,
    
    /** Indica se está carregando */
    isLoading: PropTypes.bool.isRequired,
    
    /** Erro ocorrido (null se não houver) */
    error: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Período dos dados */
    periodo: PropTypes.string,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** ID para testes */
    testId: PropTypes.string,
};

WorkloadChart.defaultProps = {
    data: null,
    error: null,
    periodo: 'Próximos 7 dias',
    className: '',
    testId: null,
};

export default WorkloadChart;

